import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaFile, FaFileCsv, FaFilePdf } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import {
  useGetMyProfile,
  useGetMyStatementFiles,
  useGetMyStatements,
} from "../api/queries";
import currencies from "../helpers/currency";
import useGetColours from "../helpers/getColours";
import { sendStatementInfo } from "../helpers/helpers";
// import AppState from "../store/appstate";
import useAppStore from "../store/appStateZustand.js";
import LoadingSmall from "./LoadingSmall";
import RebrandPrimaryButton from "./RebrandPrimaryButton";
import Table from "./Table";

function formatCurrencyString(code, val, currencies, tenantCurrencyLocale, tenantCurrencyCode) {
  return code && currencies[code]
    ? val.toLocaleString(
        currencies[code].locale,
        {
          style: "currency",
          currency: code,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      )
    : val.toLocaleString(
        tenantCurrencyLocale,
        {
          style: "currency",
          currency: code ?? tenantCurrencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        },
      );
}

function StatementNameCell({ row }) {
  return (
    <>
      <p>{row?.values?.colStatementName}</p>
      <small className="hidden md:inline">
        {"Published "}
        {row?.original.publishdate
          && row?.original.publishdate !== "0000-00-00"
          ? format(Date.parse(row?.original.publishdate), "yyyy-MM-dd")
          : "unknown"}
      </small>
    </>
  );
}

function StatementBalanceCell({ row, currencies, tenantCurrencyLocale, tenantCurrencyCode }) {
  return (
    <p className="text-sm">
      {formatCurrencyString(
        row?.original.currencycode,
        row?.original.closingbalanceclientcurrency ?? row?.original.closingbalance,
        currencies,
        tenantCurrencyLocale,
        tenantCurrencyCode,
      )}
    </p>
  );
}

function StatementFilesCell({ row, setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t }) {
  return (
    <div className="flex flex-col items-start justify-start w-full gap-2 lg:flex-row lg:items-center">
      {row?.values?.colStatementFiles && (
        <div>
          <RebrandPrimaryButton
            id="btnStatementsViewFiles"
            onClick={() => {
              setDownloads([]);
              setUuid(row?.original.uniqueidentifier);
            }}
          >
            {t("other:view-files-button")}
          </RebrandPrimaryButton>
        </div>
      )}
      {uuid === row?.original.uniqueidentifier && (
        <div className="flex flex-row flex-wrap justify-start flex-auto w-3/4 gap-3 lg:justify-center">
          {files.isLoading && <LoadingSmall />}
          {files.isSuccess && (
            <>
              {downloads.map(d => (
                <React.Fragment key={d.filename}>
                  <RebrandPrimaryButton
                    onClick={() => handleDocumentOpen(d.presignedurl, d.filename, d.mimetype)}
                  >
                    <div className="flex items-center">
                      <span className="mr-2 text-sm">
                        {getTypeIcon(d.mimetype)}
                      </span>
                      <span className="hidden sm:inline">{d.filename}</span>
                    </div>
                  </RebrandPrimaryButton>
                </React.Fragment>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BalanceCellWrapper({ currencies, tenantCurrencyLocale, tenantCurrencyCode, ...props }) {
  return (
    <StatementBalanceCell
      {...props}
      currencies={currencies}
      tenantCurrencyLocale={tenantCurrencyLocale}
      tenantCurrencyCode={tenantCurrencyCode}
    />
  );
}

function FilesCellWrapper({ setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t, ...props }) {
  return (
    <StatementFilesCell
      {...props}
      setDownloads={setDownloads}
      setUuid={setUuid}
      uuid={uuid}
      files={files}
      downloads={downloads}
      handleDocumentOpen={handleDocumentOpen}
      getTypeIcon={getTypeIcon}
      t={t}
    />
  );
}

function BalanceCellWithProps({ currencies, tenantCurrencyLocale, tenantCurrencyCode, ...props }) {
  return (
    <BalanceCellWrapper
      {...props}
      currencies={currencies}
      tenantCurrencyLocale={tenantCurrencyLocale}
      tenantCurrencyCode={tenantCurrencyCode}
    />
  );
}

function FilesCellWithProps({ setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t, ...props }) {
  return (
    <FilesCellWrapper
      {...props}
      setDownloads={setDownloads}
      setUuid={setUuid}
      uuid={uuid}
      files={files}
      downloads={downloads}
      handleDocumentOpen={handleDocumentOpen}
      getTypeIcon={getTypeIcon}
      t={t}
    />
  );
}

function createBalanceCell(currencies, tenantCurrencyLocale, tenantCurrencyCode) {
  const MemoizedCell = React.memo(props => (
    <BalanceCellWithProps
      {...props}
      currencies={currencies}
      tenantCurrencyLocale={tenantCurrencyLocale}
      tenantCurrencyCode={tenantCurrencyCode}
    />
  ));
  return MemoizedCell;
}

function createFilesCell(setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t) {
  const MemoizedCell = React.memo(props => (
    <FilesCellWithProps
      {...props}
      setDownloads={setDownloads}
      setUuid={setUuid}
      uuid={uuid}
      files={files}
      downloads={downloads}
      handleDocumentOpen={handleDocumentOpen}
      getTypeIcon={getTypeIcon}
      t={t}
    />
  ));
  return MemoizedCell;
}

function StatementsTable() {
  const { t } = useTranslation("other");
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const statements = useGetMyStatements({ aac: selectedAccount });
  const profile = useGetMyProfile();

  const [uuid, setUuid] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const files = useGetMyStatementFiles({ aac: selectedAccount, suid: uuid });
  const { highlightColour } = useGetColours();
  // const tenantCurrencyCode = AppState.useState(s => s.tenantCurrencyCode);
  // const tenantCurrencyLocale = AppState.useState(s => s.tenantCurrencyLocale);
  const tenantCurrencyCode = useAppStore(s => s.tenantCurrencyCode);
  const tenantCurrencyLocale = useAppStore(s => s.tenantCurrencyLocale);
  const isTunecore = useUIStateStore(state => state.isTunecore);

  useEffect(() => {
    if (
      files.isSuccess
      && files?.data?.statementsFiles
      && downloads.length === 0
    ) {
      if (files?.data?.statementsFiles.length === 0) {
        return;
      }

      const fileArray = JSON.parse(
        JSON.stringify(files?.data?.statementsFiles),
      );
      // sort by filename

      fileArray.sort(
        (a, b) => a.mimetype - b.mimetype || b.filename - a.filename,
      );
      setDownloads(fileArray);
    }
  }, [files.isSuccess, downloads, files, setDownloads]);

  const handleDocumentOpen = React.useCallback(async (url, filename, mimetype) => {
    window.open(url, "_blank");
    const userEmail = profile?.data?.email;

    sendStatementInfo({
      eventName: "pe14489857_april_2024_statement_download_pdf",
      properties: {
        email: userEmail,
        documentName: filename,
        documentType: mimetype,
      },
    });
  }, [profile?.data?.email]);

  const getTypeIcon = React.useCallback((mime) => {
    switch (mime) {
      case "application/pdf":
        return (
          <div className="flex flex-row items-center text-xxs md:text-sm">
            <FaFilePdf />
            {" "}
            <p className="pl-1 sm:hidden">PDF</p>
          </div>
        );
      case "text/csv":
        return (
          <div className="flex flex-row items-center text-xxs md:text-sm">
            <FaFileCsv />
            {" "}
            <p className="pl-1 sm:hidden">CSV</p>
          </div>
        );
      default:
        return (
          <div className="flex flex-row items-center text-xxs md:text-sm">
            <FaFile />
          </div>
        );
    }
  }, []);

  const balanceCell = React.useMemo(
    () => createBalanceCell(currencies, tenantCurrencyLocale, tenantCurrencyCode),
    [tenantCurrencyLocale, tenantCurrencyCode],
  );

  const filesCell = React.useMemo(
    () => createFilesCell(setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t),
    [setDownloads, setUuid, uuid, files, downloads, handleDocumentOpen, getTypeIcon, t],
  );

  const columns = React.useMemo(() => [
    {
      Header: t("common:_name"),
      accessor: "name",
      width: "25%",
      id: "colStatementName",
      Cell: StatementNameCell,
    },
    {
      Header: t("other:closing-balance"),
      accessor: "closingbalance",
      width: "25%",
      id: "colStatementBalance",
      Cell: balanceCell,
    },
    {
      Header: t("other:statement-table-header-files"),
      accessor: "hasstatement",
      width: "50%",
      id: "colStatementFiles",
      disableSortBy: true,
      Cell: filesCell,
    },
  ], [t, balanceCell, filesCell]);

  return (
    <>
      <div className={`${isTunecore ? "bg-tunecore-dark-element" : " bg-white"} rounded-lg p-4 lg:p-8`}>
        {statements.isLoading && <LoadingSmall />}
        {statements.data?.total >= 1
          ? (
              <div>
                <Table
                  area="statements"
                  data={statements?.data?.statements}
                  columns={columns}
                  min={5}
                  highlightColour={highlightColour}
                  revealComponent={null}
                  searchSuffix={t("other:statements-search-suffix")}
                  bgClass={`${isTunecore ? "bg-tunecore-dark-element" : "bg-white"} rounded-lg p-0`}
                />
              </div>
            )
          : (
              <div>
                <p>No statements available.</p>
              </div>
            )}
      </div>
    </>
  );
}

export default StatementsTable;
